import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CreditCard, Lock } from 'lucide-react';
import { motion } from 'framer-motion';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import axios from 'axios';
import { useCart } from '@/context/CartContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Checkout = () => {
  const navigate = useNavigate();
  const { items, total, clearCart } = useCart();
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    customer_email: '',
    customer_name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    postal_code: '',
    country: 'United Kingdom',
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateForm = () => {
    if (!formData.customer_email || !formData.customer_name || !formData.address_line1 || !formData.city || !formData.postal_code) {
      setError('Please fill in all required fields');
      return false;
    }
    return true;
  };

  const handleStripeCheckout = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/checkout/stripe`, {
        items: items.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity
        })),
        customer_email: formData.customer_email,
        customer_name: formData.customer_name,
        shipping_address: {
          line1: formData.address_line1,
          line2: formData.address_line2,
          city: formData.city,
          postal_code: formData.postal_code,
          country: formData.country,
        },
        origin_url: window.location.origin,
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (err) {
      setError('Failed to create checkout session. Please try again.');
      console.error('Checkout error:', err);
    } finally {
      setLoading(false);
    }
  };

  const createPayPalOrder = async () => {
    if (!validateForm()) return;

    try {
      const response = await axios.post(`${API}/checkout/paypal`, {
        items: items.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity
        })),
        customer_email: formData.customer_email,
        customer_name: formData.customer_name,
        shipping_address: {
          line1: formData.address_line1,
          line2: formData.address_line2,
          city: formData.city,
          postal_code: formData.postal_code,
          country: formData.country,
        },
        origin_url: window.location.origin,
      });

      return response.data.order_id;
    } catch (err) {
      setError('Failed to create PayPal order');
      throw err;
    }
  };

  const onPayPalApprove = async (data, actions) => {
    try {
      const orderId = await createPayPalOrder();
      await axios.post(`${API}/checkout/paypal/${orderId}/capture`, null, {
        params: { paypal_order_id: data.orderID }
      });
      clearCart();
      navigate('/checkout/success?order_id=' + orderId);
    } catch (err) {
      setError('Payment failed. Please try again.');
    }
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen pt-32 pb-24 bg-white">
        <div className="container-custom max-w-2xl text-center">
          <h1 className="text-4xl font-bold tracking-tight">
            Your Cart is Empty
          </h1>
          <p className="mt-4 text-neutral-500">Add some items to proceed to checkout.</p>
          <button
            onClick={() => navigate('/shop')}
            className="btn-primary mt-8"
          >
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="min-h-screen pt-24 md:pt-32 pb-24 bg-white">
      <div className="container-custom max-w-6xl">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          data-testid="checkout-back-button"
          className="inline-flex items-center gap-2 text-neutral-400 hover:text-black uppercase tracking-wider text-sm mb-12 transition-colors"
        >
          <ArrowLeft size={16} />
          Back
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Form Section */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h1 className="text-4xl font-bold tracking-tight">
                <span className="text-[#e84628]">Checkout</span>
              </h1>
              <p className="mt-2 text-neutral-500">Complete your order</p>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mt-10"
            >
              <h2 className="font-semibold uppercase tracking-wider text-sm mb-6">
                Contact Information
              </h2>
              <div className="space-y-4">
                <input
                  type="email"
                  name="customer_email"
                  value={formData.customer_email}
                  onChange={handleInputChange}
                  placeholder="Email address *"
                  required
                  data-testid="checkout-email"
                  className="input-underline"
                />
                <input
                  type="text"
                  name="customer_name"
                  value={formData.customer_name}
                  onChange={handleInputChange}
                  placeholder="Full name *"
                  required
                  data-testid="checkout-name"
                  className="input-underline"
                />
              </div>
            </motion.div>

            {/* Shipping Address */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mt-10"
            >
              <h2 className="font-semibold uppercase tracking-wider text-sm mb-6">
                Shipping Address
              </h2>
              <div className="space-y-4">
                <input
                  type="text"
                  name="address_line1"
                  value={formData.address_line1}
                  onChange={handleInputChange}
                  placeholder="Address line 1 *"
                  required
                  data-testid="checkout-address1"
                  className="input-underline"
                />
                <input
                  type="text"
                  name="address_line2"
                  value={formData.address_line2}
                  onChange={handleInputChange}
                  placeholder="Address line 2 (optional)"
                  data-testid="checkout-address2"
                  className="input-underline"
                />
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="City *"
                    required
                    data-testid="checkout-city"
                    className="input-underline"
                  />
                  <input
                    type="text"
                    name="postal_code"
                    value={formData.postal_code}
                    onChange={handleInputChange}
                    placeholder="Postal code *"
                    required
                    data-testid="checkout-postal"
                    className="input-underline"
                  />
                </div>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  placeholder="Country *"
                  required
                  data-testid="checkout-country"
                  className="input-underline"
                />
              </div>
            </motion.div>

            {/* Payment Method */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mt-10"
            >
              <h2 className="font-semibold uppercase tracking-wider text-sm mb-6">
                Payment Method
              </h2>
              <div className="space-y-3">
                <button
                  onClick={() => setPaymentMethod('stripe')}
                  data-testid="payment-stripe"
                  className={`w-full p-4 border flex items-center gap-3 transition-all ${
                    paymentMethod === 'stripe'
                      ? 'border-black bg-neutral-50'
                      : 'border-neutral-200 hover:border-neutral-400'
                  }`}
                >
                  <CreditCard size={20} />
                  <span className="font-medium">Credit / Debit Card</span>
                  <span className="ml-auto text-neutral-400 text-sm">Stripe</span>
                </button>
                <button
                  onClick={() => setPaymentMethod('paypal')}
                  data-testid="payment-paypal"
                  className={`w-full p-4 border flex items-center gap-3 transition-all ${
                    paymentMethod === 'paypal'
                      ? 'border-black bg-neutral-50'
                      : 'border-neutral-200 hover:border-neutral-400'
                  }`}
                >
                  <span className="font-bold text-[#0070ba]">Pay</span>
                  <span className="font-bold text-[#003087]">Pal</span>
                </button>
              </div>
            </motion.div>

            {/* Error */}
            {error && (
              <p className="mt-6 text-red-500 text-sm">{error}</p>
            )}

            {/* Submit */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mt-10"
            >
              {paymentMethod === 'stripe' ? (
                <button
                  onClick={handleStripeCheckout}
                  disabled={loading}
                  data-testid="checkout-submit"
                  className="w-full bg-[#e84628] text-white py-5 font-semibold uppercase tracking-wider flex items-center justify-center gap-3 hover:bg-[#d63d20] transition-colors disabled:opacity-50"
                >
                  {loading ? (
                    <div className="spinner w-5 h-5" />
                  ) : (
                    <>
                      <Lock size={18} />
                      Pay £{total.toFixed(2)}
                    </>
                  )}
                </button>
              ) : (
                <PayPalScriptProvider options={{ clientId: "test", currency: "GBP" }}>
                  <PayPalButtons
                    style={{ layout: "vertical", color: "black", shape: "rect", label: "pay" }}
                    createOrder={createPayPalOrder}
                    onApprove={onPayPalApprove}
                    onError={() => setError('PayPal payment failed')}
                  />
                </PayPalScriptProvider>
              )}
            </motion.div>

            <p className="mt-6 text-neutral-400 text-sm flex items-center gap-2">
              <Lock size={14} />
              Your payment information is secure and encrypted
            </p>
          </div>

          {/* Order Summary */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-neutral-50 border border-neutral-200 p-8"
            >
              <h2 className="font-semibold uppercase tracking-wider text-sm mb-6">
                Order Summary
              </h2>

              <div className="space-y-6">
                {items.map((item) => (
                  <div key={item.product_id} className="flex gap-4">
                    <div className="w-16 h-16 bg-neutral-200 flex-shrink-0">
                      {item.image && (
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-sm">
                        {item.name}
                      </h3>
                      <p className="text-neutral-400 text-sm">Qty: {item.quantity}</p>
                    </div>
                    <p className="font-medium">
                      £{(item.price * item.quantity).toFixed(2)}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-8 pt-6 border-t border-neutral-200 space-y-3">
                <div className="flex justify-between text-neutral-500">
                  <span>Subtotal</span>
                  <span>£{total.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-neutral-500">
                  <span>Shipping</span>
                  <span className="text-green-600">Free</span>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-neutral-200 flex justify-between">
                <span className="font-semibold uppercase">Total</span>
                <span className="text-2xl font-bold">
                  £{total.toFixed(2)}
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
